import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

export type StarRatingProps = Immutable<{
    rating: number;
    number?: number | null;
    className?: string;
}>;

type StarType = "full" | "half" | "outline";

const FullStar = (): React.JSX.Element => (
    <svg
        className="star-rating__star"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 50 50"
        aria-hidden="true"
    >
        <path d="M49.2,21.1L38.3,31.7l2.6,15c0,0.2,0,0.4,0,0.6c0,0.8-0.4,1.5-1.2,1.5c-0.4,0-0.8-0.2-1.2-0.4L25,41.4l-13.5,7.1 c-0.4,0.2-0.8,0.4-1.2,0.4c-0.9,0-1.3-0.7-1.3-1.5c0-0.2,0-0.4,0.1-0.6l2.6-15L0.8,21.1C0.4,20.7,0,20.2,0,19.6 c0-0.9,0.9-1.3,1.7-1.4l15.1-2.2l6.8-13.7c0.3-0.6,0.8-1.2,1.5-1.2c0.7,0,1.2,0.7,1.5,1.2l6.8,13.7l15.1,2.2 c0.7,0.1,1.7,0.5,1.7,1.4C50,20.2,49.6,20.7,49.2,21.1z" />
    </svg>
);

const HalfStar = (): React.JSX.Element => (
    <svg
        className="star-rating__star"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 50 50"
        aria-hidden="true"
    >
        <path d="M38.3,31.7l2.6,15c0.2,1.3-0.3,2.1-1.2,2.1c-0.3,0-0.7-0.1-1.2-0.4L25,41.4l-13.5,7.1c-0.5,0.2-0.9,0.4-1.2,0.4 c-0.9,0-1.4-0.8-1.2-2.1l2.6-15L0.8,21.1c-1.3-1.3-0.9-2.6,0.9-2.8l15.1-2.2l6.8-13.7c0.4-0.8,0.9-1.2,1.5-1.2 c0.5,0,1.1,0.4,1.5,1.2l6.8,13.7l15.1,2.2c1.8,0.3,2.2,1.5,0.9,2.8L38.3,31.7z M43.4,21.4l-10.7-1.6l-2-0.3l-0.9-1.8L25,8.1V37 l1.8,0.9l9.6,5.1l-1.8-10.7l-0.4-2l1.5-1.4L43.4,21.4z" />
    </svg>
);

const OutlineStar = (): React.JSX.Element => (
    <svg
        className="star-rating__star"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 50 50"
        aria-hidden="true"
    >
        <path d="M49.2,21.1L38.3,31.7l2.6,15c0,0.2,0,0.4,0,0.6c0,0.8-0.4,1.5-1.2,1.5c-0.4,0-0.8-0.2-1.2-0.4L25,41.4l-13.5,7.1 c-0.4,0.2-0.8,0.4-1.2,0.4c-0.9,0-1.3-0.7-1.3-1.5c0-0.2,0-0.4,0.1-0.6l2.6-15L0.7,21.1C0.4,20.7,0,20.2,0,19.6 c0-0.9,0.9-1.3,1.7-1.4l15.1-2.2l6.8-13.7c0.3-0.6,0.8-1.2,1.5-1.2c0.7,0,1.2,0.7,1.5,1.2l6.8,13.7l15.1,2.2 c0.7,0.1,1.7,0.5,1.7,1.4C50,20.2,49.6,20.7,49.2,21.1z M43.4,21.4l-12.7-1.9L25,8.1l-5.7,11.5L6.6,21.4l9.2,8.9L13.6,43L25,37 l11.3,6l-2.2-12.6L43.4,21.4z" />
    </svg>
);

const getStarType = (rating: number, starIndex: number): StarType => {
    if (rating >= starIndex + 0.75) {
        return "full";
    }
    if (rating >= starIndex + 0.25) {
        return "half";
    }
    return "outline";
};

const renderStar = (type: StarType): React.JSX.Element => {
    switch (type) {
        case "full":
            return <FullStar />;
        case "half":
            return <HalfStar />;
        case "outline":
            return <OutlineStar />;
    }
};

export const StarRating = (props: StarRatingProps): React.JSX.Element => {
    const rating = props.rating;
    const a11yLabel = t`Rated ${rating} out of 5 stars`;
    const outerClassName = props.className
        ? `star-rating ${props.className}`
        : "star-rating";
    const number = props.number;
    const showNumber = number !== null && number !== undefined;
    const formattedNumber = showNumber
        ? new Intl.NumberFormat().format(number)
        : "";

    return (
        <div className={outerClassName}>
            <div className="star-rating__stars">
                <span
                    className="ada-screenreader-only"
                    aria-label="star rating"
                >
                    {a11yLabel}
                </span>
                {[0, 1, 2, 3, 4].map((starIndex) => (
                    <React.Fragment key={starIndex}>
                        {renderStar(getStarType(rating, starIndex))}
                    </React.Fragment>
                ))}
            </div>
            {showNumber && (
                <div className="star-rating__number">{formattedNumber}</div>
            )}
        </div>
    );
};
